/* eslint-disable import/order */
/* eslint-disable camelcase */
import { useState, useEffect } from 'react';

import {
  Card,
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';

import { PATH_DASHBOARD } from '../../routes/paths';

import useSettings from '../../hooks/useSettings';

import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Label from 'components/Label';

// =====================================================

export default function ComplianceSystemHealth() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const theme = useTheme();

  const [data, setData] = useState([]);

  const [state, setState] = useState({
    loading: false,
    error: null
  });

  useEffect(() => {
    getInitData();
  }, []);

  // =====================================================
  // FETCH DATA
  // =====================================================
  async function getInitData() {
    setState({ loading: true, error: null });

    try {
      const response = await axios.get(CRUD.compliance.health);

      if (isMountedRef.current) {
        setData(response.data || []);
      }

      setState({ loading: false, error: null });
    } catch (error) {
      setState({
        loading: false,
        error: getErrorMessage(error)
      });
    }
  }

  // =====================================================
  // TRANSFORM DATA
  // =====================================================
  const grouped = groupByService(data);

  function getHealthColor(rate) {
    if (rate >= 98) return 'success';
    if (rate >= 90) return 'warning';
    return 'error';
  }

  // =====================================================

  return (
    <Page title="System Health">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="System Health"
          links={[
            { name: 'Compliance', href: PATH_DASHBOARD.compliance.root },
            { name: 'System Health' },
          ]}
        />

        {/* ERROR */}
        {state.error && (
          <Card>
            <Box p={5} textAlign="center">
              <Typography color="error">{state.error}</Typography>
            </Box>
          </Card>
        )}

        {/* LOADING */}
        {state.loading && (
          <Box display="flex" justifyContent="center" mt={10}>
            <CircularProgress />
          </Box>
        )}

        {/* CONTENT */}
        {!state.loading && (
          <Grid container spacing={3}>

            {Object.keys(grouped).map((service) => {
              const svc = grouped[service];

              return (
                <Grid item xs={12} md={4} key={service}>
                  <Card sx={{ p: 3 }}>

                    <Typography variant="h6">
                      {service.toUpperCase()}
                    </Typography>

                    <Box mt={2}>
                      <Typography variant="body2">
                        Total Requests: {svc.total}
                      </Typography>

                      <Typography variant="body2">
                        Success: {svc.success}
                      </Typography>

                      <Typography variant="body2">
                        Failures: {svc.fail}
                      </Typography>
                    </Box>

                    <Box mt={2}>
                      <Label color={getHealthColor(svc.successRate)}>
                        Uptime: {svc.successRate}%
                      </Label>
                    </Box>

                    {svc.successRate < 90 && (
                      <Box mt={1}>
                        <Label color="error">
                          Service Degraded
                        </Label>
                      </Box>
                    )}

                  </Card>
                </Grid>
              );
            })}

            {Object.keys(grouped).length === 0 && (
              <Grid item xs={12}>
                <Card>
                  <Box p={5} textAlign="center">
                    <Typography>No service logs available</Typography>
                  </Box>
                </Card>
              </Grid>
            )}

          </Grid>
        )}
      </Container>
    </Page>
  );
}

// =====================================================
// HELPERS
// =====================================================

function groupByService(data) {
  const map = {};

  data.forEach((row) => {
    const service = row.service || 'unknown';

    if (!map[service]) {
      map[service] = {
        success: 0,
        fail: 0,
        total: 0,
        successRate: 0
      };
    }

    map[service].total += Number(row.total);

    if (row.status === 'success') {
      map[service].success += Number(row.total);
    } else {
      map[service].fail += Number(row.total);
    }
  });

  Object.keys(map).forEach((service) => {
    const svc = map[service];

    svc.successRate = svc.total
      ? Math.round((svc.success / svc.total) * 100)
      : 0;
  });

  return map;
}